import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const EMPTY = { full_name: '', email: '', phone: '', company: '', address: '', send_welcome_email: false };

const FIELDS = [
  { key: 'full_name', label: 'Full name', required: true, placeholder: 'Jane Agent' },
  { key: 'email', label: 'Email', required: true, type: 'email', placeholder: 'jane@brokerage.com' },
  { key: 'phone', label: 'Phone', placeholder: '(928) 555-0123' },
  { key: 'company', label: 'Brokerage (optional)', placeholder: 'Realty One Group' },
  { key: 'address', label: 'Address (optional)', placeholder: 'Mailing / office address' },
];

export const AddClientDialog = ({ open, onOpenChange, goToNewOrder = false }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          email: form.email.trim(),
          phone: form.phone || null,
          company: form.company || null,
          address: form.address || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data.detail) ? 'Please enter a valid email address' : data.detail;
        throw new Error(msg || 'Failed to add client');
      }
      if (data.existing) toast.info(`${data.client.email} already has an account — opening that client`);
      else if (data.welcome_email_sent) toast.success(`Client added and set-password email sent to ${data.client.email}`);
      else if (data.welcome_email_error) toast.error('Client added, but the welcome email failed', { description: data.welcome_email_error, duration: 10000 });
      else toast.success(`Client ${data.client.full_name || data.client.email} added`);
      setForm(EMPTY);
      onOpenChange(false);
      navigate(`/admin/clients/${data.client.id}${goToNewOrder ? '/new-order' : ''}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1724] border-white/10 text-white sm:max-w-md" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-light tracking-wide">
            <UserPlus className="h-5 w-5 text-[#C6A14A]" /> {goToNewOrder ? 'New Order for Client' : 'Add Client'}
          </DialogTitle>
          <DialogDescription className="text-[#E8E4DE]/60">
            {goToNewOrder
              ? 'Enter the client. If they already have an account it is selected; otherwise one is created (no password). Then fill in the order.'
              : 'Creates their account without a password (email already confirmed). Optionally email them a link to set their password.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label className="text-[#E8E4DE]/80">{f.label}</Label>
              <Input
                type={f.type || 'text'}
                required={f.required}
                value={form[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="bg-[#050816] border-white/10 text-white placeholder:text-white/30"
                data-testid={`add-client-${f.key}-input`}
              />
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm text-[#E8E4DE] pt-1">
            <Checkbox checked={form.send_welcome_email} onCheckedChange={(v) => setForm({ ...form, send_welcome_email: !!v })}
              data-testid="add-client-send-welcome-checkbox" />
            Email them a set-your-password link (from orders@rcstudios.net)
          </label>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-white/10 bg-transparent text-[#E8E4DE] hover:bg-white/5" data-testid="add-client-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={saving}
              className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="add-client-submit-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {goToNewOrder ? 'Continue to Order' : 'Add Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
